import React, { useState, useContext, useEffect, useRef } from "react";

import Swal from "sweetalert2";
import { Button, Card, Alert, Row, Col, Spinner } from "react-bootstrap";
import { Buffer } from "buffer";

import {
  GetWhatsappQrCode,
  ReconnectWhatsappAccount,
  CheckAccountStatus,
  RequestWhatsappCode,
} from "../../../services/WaBottyServices";
import UserContext from "../../../contexts/UserContext";
import { formatPhoneForAPI } from "../../../helpers/Helpers";

export default function WhatsappConnect(props) {
  const { user } = useContext(UserContext);

  const [whatsappQrCode, setWhatsappQrCode] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const checkInterval = useRef(null);
  const countdownInterval = useRef(null);
  const isMountedRef = useRef(true);
  
  // Estados para método de conexão
  const [connectionMethod, setConnectionMethod] = useState(null); // 'qrcode' ou 'code'
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(""); // Código gerado pela API
  // Limpar intervalos e resetar estados
  const clearIntervals = () => {
    if (checkInterval.current) {
      clearInterval(checkInterval.current);
      checkInterval.current = null;
    }
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
    setIsCheckingConnection(false);
  };

  // Verificar status da conexão
  const checkConnectionStatus = async () => {
    if (!props.phoneNumber) return null;

    try {
      const result = await CheckAccountStatus(
        props.phoneNumber,
        props.agentId || null
      );

      // ✅ Se conectado, retornar dados da conta para handleConnectionSuccess
      if (result.connected && result.data?.status === "WORKING") {
        return {
          phone: props.phoneNumber,
          formattedPhone: props.phoneNumber,
          status: "WORKING",
          connected: true,
          data: result.data,
        };
      }

      return null;
    } catch (error) {
      console.error("Erro ao verificar status da conexão:", error);
      return null;
    }
  };

  // Iniciar countdown e verificação
  const startCountdown = () => {
    setCountdown(15);

    // Countdown visual
    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          countdownInterval.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // ✅ Após countdown, iniciar verificação contínua a cada 2 segundos
    setTimeout(() => {
      startConnectionChecking();
    }, 5000);
  };

  // ✅ Verificar conexão continuamente a cada 2 segundos
  const startConnectionChecking = async () => {
    if (!isMountedRef.current) return;

    setIsCheckingConnection(true);

    // ✅ Criar intervalo para verificar a cada 2 segundos
    checkInterval.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearIntervals();
        return;
      }

      try {
        const connectionData = await checkConnectionStatus();

        if (!isMountedRef.current) return; // Verificar se ainda está montado

        if (connectionData && connectionData.connected) {
          // ✅ Sucesso - status "WORKING" encontrado
          console.log("✅ WhatsApp conectado com sucesso!", connectionData);
          clearIntervals();

          if (props.onConnectionSuccess) {
            // ✅ Passar dados da conexão para o callback
            props.onConnectionSuccess({
              phone: connectionData.phone,
              status: connectionData.status,
              uid: connectionData.data?.uid || null,
            });
          }
          return;
        }

        console.log("🔄 Ainda aguardando conexão WhatsApp...");
      } catch (error) {
        console.error("Erro na verificação periódica:", error);
        // Continua tentando mesmo com erro
      }
    }, 3000); // ✅ Verificar a cada 2 segundos

    // ✅ Timeout de segurança: parar após  2 minutos se não conectar
    setTimeout(() => {
      if (checkInterval.current && isMountedRef.current) {
        console.log("⏰ Timeout atingido, parando verificação");
        clearIntervals();
        setIsCheckingConnection(false);
        handleRetryOrFailure();
      }
    }, 120000); // 2 minutos timeout
  };

  // Tentar novamente ou falhar definitivamente
  const handleRetryOrFailure = () => {
    if (!isMountedRef.current) return;

    const currentAttempt = attempts + 1;

    if (currentAttempt < 3) {
      // Tentar novamente
      setAttempts(currentAttempt);
      setWhatsappQrCode(null);
      setIsLoading(true);

      // Gerar novo QR Code após pequeno delay
      setTimeout(() => {
        if (isMountedRef.current) {
          if (connectionMethod === 'qrcode') {
            handleConnect();
          }
        }
      }, 1000);
    } else {
      // Falha final
      if (props.onConnectionFailure) {
        props.onConnectionFailure();
      }
    }
  };

  // Função para solicitar código de verificação
  const handleRequestCode = async () => {
    setIsRequestingCode(true);
    
    try {
      const result = await RequestWhatsappCode(props.phoneNumber);
      
      if (result.success) {
        setCodeRequested(true);
        setGeneratedCode(result.code || "");
        
        Swal.fire({
          icon: "success",
          title: "Código Gerado!",
          html: `
            <div style="text-align: center;">
              <p>Código de verificação gerado para ${props.phoneNumber}:</p>
              <div style="
                background: #f0f8f4; 
                border: 2px dashed #25d366; 
                border-radius: 8px; 
                padding: 16px; 
                margin: 16px 0;
                font-size: 24px;
                font-weight: bold;
                color: #25d366;
                letter-spacing: 0.3em;
              ">
                ${result.code || "N/A"}
              </div>
              <p style="color: #666; font-size: 14px; margin: 8px 0;">
                Aguarde... iniciando verificação de conexão automaticamente.
              </p>
            </div>
          `,
          confirmButtonText: "Continuar",
          confirmButtonColor: "#25d366",
          allowOutsideClick: false,
          allowEscapeKey: false
        })
        startConnectionChecking();
      } else {
        // Construir mensagem de erro com sugestão se disponível
        let errorMessage = result.error || "Não foi possível solicitar o código de verificação.";
        let errorTitle = "Erro ao Solicitar Código";
        
        // Personalizar título baseado no código de erro
        if (result.error_code) {
          switch (result.error_code) {
            case "SESSION_DISCONNECTED":
              errorTitle = "Sessão Desconectada";
              break;
            case "SESSION_NOT_FOUND":
              errorTitle = "Sessão Não Encontrada";
              break;
            case "QR_CODE_REQUIRED":
              errorTitle = "Autenticação Necessária";
              break;
            case "BROWSER_ERROR":
              errorTitle = "Erro do Sistema";
              break;
            default:
              // Manter título padrão para códigos não mapeados
              break;
          }
        }

        // Adicionar sugestão se disponível
        if (result.suggestion) {
          errorMessage += `\n\n💡 ${result.suggestion}`;
        }

        Swal.fire({
          icon: "error",
          title: errorTitle,
          text: errorMessage,
          confirmButtonText: "Entendi",
          confirmButtonColor: "#dc3545",
          html: result.suggestion ? `
            <div style="text-align: left;">
              <p><strong>Problema:</strong></p>
              <p style="margin-bottom: 16px;">${result.error}</p>
              
              <p><strong>💡 Como resolver:</strong></p>
              <p style="background: #f8f9fa; padding: 12px; border-radius: 6px; border-left: 4px solid #007bff; margin: 0;">
                ${result.suggestion}
              </p>
            </div>
          ` : undefined,
        });
      }
    } catch (error) {
      console.error("Erro ao solicitar código:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro inesperado. Tente novamente.",
        confirmButtonText: "Fechar",
      });
    } finally {
      setIsRequestingCode(false);
    }
  };


  // Cleanup dos intervalos quando componente for desmontado
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearIntervals();
    };
  }, []);

  const handleConnect = () => {
    setConnectionMethod('qrcode');
    setIsLoading(true);
    // ✅ Adicionar agent_id aos parâmetros
    GetWhatsappQrCode({
      phone: props.phoneNumber,
      agent_id: props.agentId || null,
    })
      .then((result) => {
        console.log("QR Code gerado com sucesso:", result);
        let base64Image = Buffer.from(result, "binary").toString("base64");
        setWhatsappQrCode(base64Image);
        setIsLoading(false);
        startCountdown();
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Erro ao gerar QR Code:", error);

        if (props.setNotify) {
          props.setNotify({
            type: "error",
            message: "Erro ao gerar QR Code. Tente novamente.",
            title: "Erro",
            visible: true,
          });
        }
      });
  };

  const handleConnectWithCode = () => {
    setConnectionMethod('code');
    handleRequestCode();
  };

  const handleReconnect = () => {
    setIsLoading(true);
    ReconnectWhatsappAccount({
      b_token: props.data.token,
      unique: props.data.unique,
    })
      .then((result) => {
        if (result.status === 200 && result.data?.qrimagelink) {
          // ✅ Adicionar agent_id aos parâmetros na reconexão
          GetWhatsappQrCode({
            qrstring: result.data.qrstring,
            agent_id: props.agentId || null,
          }).then((result) => {
            let base64Image = Buffer.from(result, "binary").toString("base64");
            setWhatsappQrCode(base64Image);
            setIsLoading(false);
            startCountdown();
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Erro na reconexão:", error);
        Swal.fire({
          title: "Erro!",
          text: "Ocorreu um erro inesperado ao tentar gerar o QrCode para conexão, por favor, tente novamente. Se o problema persistir, contate nosso setor de suporte.",
          icon: "error",
          confirmButtonText: "Entendi",
        });
      });
  };

  const isConnecting = props.connectOrReconnect.current === "connect";

  // Renderizar interface de verificação de código
  const renderCodeVerificationInterface = () => (
    <div className="text-center py-4">
      <h5 className="fw-bold text-dark mb-3">
        📱 Verificando Conexão WhatsApp
      </h5>
      <p className="text-muted mb-3">
        Código gerado para <strong>{props.phoneNumber}</strong>:
      </p>
      
      {/* Exibir código gerado */}
      {generatedCode && (
        <div className="mb-4">
          <div 
            style={{
              background: "#f0f8f4",
              border: "2px dashed #25d366",
              borderRadius: "12px",
              padding: "20px",
              margin: "20px auto",
              maxWidth: "300px",
              fontSize: "28px",
              fontWeight: "bold",
              color: "#25d366",
              letterSpacing: "0.3em",
              fontFamily: "monospace",
              position: "relative"
            }}
          >
            {generatedCode}
            <Button
              variant="outline-success"
              size="sm"
              className="ms-3"
              style={{
                position: "absolute",
                top: "50%",
                right: "10px",
                transform: "translateY(-50%)",
                padding: "4px 8px",
                fontSize: "12px"
              }}
              onClick={() => {
                navigator.clipboard.writeText(generatedCode);
                Swal.fire({
                  toast: true,
                  position: "top-end",
                  icon: "success",
                  title: "Código copiado!",
                  showConfirmButton: false,
                  timer: 1500
                });
              }}
            >
              <i className="fa fa-copy"></i>
            </Button>
          </div>
        </div>
      )}

      {/* Status de verificação */}
      {isCheckingConnection ? (
        <Alert variant="info" className="mb-3">
          <div className="d-flex align-items-center justify-content-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <strong>Verificando conexão...</strong>
          </div>
          <small className="text-muted mt-1 d-block">
            Aguarde enquanto verificamos se sua conexão foi estabelecida
          </small>
        </Alert>
      ) : (
        <Alert variant="success" className="mb-3">
          <div className="d-flex align-items-center justify-content-center">
            <i className="fa fa-check-circle me-2"></i>
            <strong>Código gerado com sucesso!</strong>
          </div>
          <small className="text-muted mt-1 d-block">
            A verificação de conexão será iniciada automaticamente
          </small>
        </Alert>
      )}

      <div className="d-flex justify-content-center">
        <Button
          variant="outline-secondary"
          onClick={() => {
            clearIntervals(); // Para a verificação se estiver rodando
            setConnectionMethod(null);
            setCodeRequested(false);
            setGeneratedCode("");
          }}
          disabled={false}
        >
          Voltar
        </Button>
      </div>
    </div>
  );

  return (
    <div id="connect-whatsapp-account" className="p-0">
      {connectionMethod === 'code' && codeRequested ? (
        renderCodeVerificationInterface()
      ) : !whatsappQrCode && !connectionMethod ? (
        <div className="text-center">
          {isLoading || isRequestingCode ? (
            <div className="py-4">
              <Spinner
                animation="border"
                variant="success"
                className="mb-3"
                style={{ width: "3rem", height: "3rem" }}
              />
              <h5 className="text-muted mb-2">
                {isRequestingCode 
                  ? "Solicitando código de verificação..."
                  : attempts > 0
                    ? `Regenerando QR Code... (Tentativa ${attempts + 1} de 3)`
                    : "Gerando QR Code..."
                }
              </h5>
              <p className="text-muted small mb-0">
                {isRequestingCode
                  ? "Enviando código para seu telefone"
                  : attempts > 0
                    ? "Tentando conectar novamente"
                    : "Aguarde enquanto preparamos sua conexão"
                }
              </p>
            </div>
          ) : (
            <div className="py-3">
              {/* Header com informações */}
              <div className="text-center mb-4">
                <h5 className="fw-bold text-dark mb-2">
                  📱 Como deseja conectar seu WhatsApp?
                </h5>
                <p className="text-muted mb-0">
                  Escolha o método de conexão de sua preferência
                </p>
              </div>

              {/* Opções de conexão */}
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Card 
                    className="h-100 border-2 cursor-pointer hover-card"
                    style={{ 
                      borderColor: '#25d366',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={handleConnect}
                  >
                    <Card.Body className="text-center p-4">
                      <div className="mb-3">
                        <i className="fa fa-qrcode" style={{ fontSize: '3rem', color: '#25d366' }}></i>
                      </div>
                      <h6 className="fw-bold text-dark mb-2">QR Code</h6>
                      <p className="text-muted small mb-0">
                        Escaneie o código com a câmera do WhatsApp
                      </p>
                      <div className="mt-3">
                        <span className="badge bg-success">Recomendado</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card 
                    className="h-100 border-2 cursor-pointer hover-card"
                    style={{ 
                      borderColor: '#128c7e',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={handleConnectWithCode}
                  >
                    <Card.Body className="text-center p-4">
                      <div className="mb-3">
                        <i className="fa fa-mobile-alt" style={{ fontSize: '3rem', color: '#128c7e' }}></i>
                      </div>
                      <h6 className="fw-bold text-dark mb-2">Código</h6>
                      <p className="text-muted small mb-0">
                        Receba um código para conectar o whatsapp
                      </p>
                      <div className="mt-3">
                        <span className="badge bg-info">Alternativo</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Informações importantes */}
              <Alert variant="info" className="mb-0">
                <div className="d-flex justify-content-center align-items-start gap-2">
                  <i className="fa fa-info-circle mt-1"></i>
                  <div>
                    <strong>📝 Antes de continuar:</strong>
                    <br />
                    • Tenha seu telefone <strong>{props.phoneNumber}</strong> em mãos
                    <br />
                    • Certifique-se de que o WhatsApp está instalado e funcionando
                    <br />
                    • Mantenha esta janela aberta durante todo o processo
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center">
          <Row className="justify-content-center">
            <Col lg={8} md={10}>
              <div className="mb-4">
                <h4 className="mb-3 text-success">
                  <i className="fa fa-qrcode me-2"></i>
                  Escaneie o QR Code
                </h4>

                {countdown > 0 ? (
                  <Alert variant="warning" className="mb-3">
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fa fa-clock me-2"></i>
                      <strong>Tempo restante: {countdown} segundos</strong>
                    </div>
                  </Alert>
                ) : (
                  isCheckingConnection && (
                    <Alert variant="info" className="mb-3">
                      <div className="d-flex align-items-center justify-content-center">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        <strong>Verificando conexão...</strong>
                      </div>
                    </Alert>
                  )
                )}
              </div>

              <div
                className="qr-code-container p-3 bg-white rounded shadow-sm mb-4"
                style={{
                  display: "inline-block",
                  border: "2px solid #25d366",
                }}
              >
                <img
                  src={`data:image/png;base64,${whatsappQrCode}`}
                  alt="QR Code WhatsApp"
                  className="img-fluid"
                  style={{ maxWidth: "250px", width: "100%" }}
                />
              </div>

              <div className="steps-guide">
                <h6 className="text-success mb-3">
                  📱 Aponte a câmera do WhatsApp para o código acima
                </h6>
                <small className="text-muted">
                  O código expira em {countdown} segundos. NÃO feche esta
                  janela!
                </small>
              </div>

              {/* Instruções rápidas */}
              <div
                className="mt-4 p-3 rounded-3"
                style={{ background: "#f8f9fa" }}
              >
                <h6 className="fw-bold text-dark mb-2">
                  ✅ Lembra dos passos:
                </h6>
                <div className="d-flex flex-column gap-1">
                  <small>
                    1️⃣ WhatsApp → Menu (3 pontos) → Dispositivos conectados
                  </small>
                  <small>2️⃣ "Conectar um dispositivo"</small>
                  <small>3️⃣ Aponte a câmera para o QR Code acima</small>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <style jsx>{`
        .qr-code-container {
          transition: all 0.3s ease;
        }

        .qr-code-container:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 25px rgba(37, 211, 102, 0.2) !important;
        }

        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        .step-item {
          transition: transform 0.2s ease;
        }

        .step-item:hover {
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .qr-code-container img {
            max-width: 200px !important;
          }

          .steps-guide .col-md-4 {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
